import { z } from 'zod';

import { foldDigitsToAscii } from '@shared/format';

import type { ContactFormValues } from '../_shared/types';

/**
 * One schema, two consumers: the form resolver and the request shape. The TS
 * type is derived with `z.infer` and never hand-written in parallel.
 *
 * **Every channel is optional, and that is the row's own rule rather than a
 * relaxed validation.** `[x]` §21.2 enumerates five channels; the public page
 * drops the row for a channel the operator left blank rather than printing «غير
 * متاح», so an empty field is a decision the operator is allowed to make.
 *
 * **The number field is digit-tolerant.** An operator on an Arabic keyboard
 * types `٠٧٧٠…`, and the value the public page puts inside `tel:` is ASCII — so
 * the fold happens in the transform, before validation, rather than the operator
 * being rejected for the glyph their keyboard produced. Nothing displayed is
 * rewritten; only what is submitted.
 *
 * **The URLs are checked for shape, not for reachability.** A malformed target
 * is a mistake the operator can fix here; whether the far end answers is not
 * something this surface can know or should claim.
 */

const trimmed = z.string().trim();

const optionalUrl = trimmed.refine((value) => value.length === 0 || /^https?:\/\/\S+$/.test(value), {
  message: 'الرابط يحتاج أن يبدأ بـ https:// وأن يكون كاملاً.',
});

/**
 * **No field carries a zod `.default()`**, and that is a typing decision with a
 * behavioural edge. A default makes the schema's input optional and its output
 * required, which splits the form into two types for no gain here: the mapping
 * module already supplies every key from the payload, so a control is never
 * mounted without a value. One type, one `useForm` generic, and an absent key is
 * a compile error rather than a silent empty string.
 */
export const contactSchema = z.object({
  supportPhone: trimmed
    .transform((value) => foldDigitsToAscii(value))
    .refine((value) => value.length === 0 || /^\+?\d{7,15}$/.test(value), {
      message: 'الرقم بالصيغة الدولية، أرقاماً فقط بعد علامة +.',
    }),
  email: trimmed.refine((value) => value.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), {
    message: 'البريد الإلكتروني يحتاج علامة @ واسم نطاق بعدها.',
  }),
  address: trimmed,
  mapLabel: trimmed,
  mapUrl: optionalUrl,
  social: z.array(z.object({ handle: trimmed, url: optionalUrl })),
});

/**
 * The schema's own output, **proved against the shape the wire mapper reads**.
 *
 * `Satisfies` fails the build if the two ever diverge — a field renamed here and
 * not there would otherwise drop silently out of the payload, and the page would
 * save a channel the operator can see they filled in.
 */
type Satisfies<T extends ContactFormValues> = T;

export type ContactValues = Satisfies<z.infer<typeof contactSchema>>;
